import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { transformRawArchitectScenes } from '../../reducers/architectScene';
import {
  ArchitectSceneInfo,
  RawArchitectSceneInfo,
  UserOrg,
} from '../../types';
import { BASE_ORG_API } from '../../utils/constants';
import { parseUrlParams } from '../../utils/driveUtil';
import { fetchWithAuth } from '../../utils/requests';

type ArchitectScenesGetResponse = {
  data: RawArchitectSceneInfo[];
  total_count: number;
};

type UpdateArchitectSceneTitleParams = {
  sceneId: ArchitectSceneInfo['id'];
  title: string;
};

type PublishArchitectSceneParams = {
  sceneId: ArchitectSceneInfo['id'];
  title: string;
};

export type ArchitectSceneFile = {
  id: number;
  name: string;
  type: string;
};

type DownloadArchitectSceneFilesParams = {
  sceneId: ArchitectSceneInfo['id'];
};

export type ArchitectSceneFileDownload = ArchitectSceneFile & {
  signedUrl: string;
};

type ArchitectSceneFilesDownloadResult = {
  files: ArchitectSceneFileDownload[];
  failureCount: number;
};

const fetchArchitectScenes = async (
  curUserOrg: UserOrg | undefined,
  orderBy: string,
  sortOrder: string,
  pageIndex: number,
  pageSize: number,
  searchParams: string,
  signal?: AbortSignal
): Promise<{ data: ArchitectSceneInfo[]; meta: { totalCount: number } }> => {
  if (!curUserOrg?.id) {
    throw new Error('Organization ID is not available');
  }

  const filters = parseUrlParams(searchParams);
  const searchTerm = new URLSearchParams(searchParams).get('q')?.trim();
  const fetchURL = new URL(
    `${BASE_ORG_API}/${curUserOrg.id}/architect_scenes/`,
    window.location.origin
  );
  fetchURL.searchParams.set('page', `${pageIndex + 1}`);
  fetchURL.searchParams.set('per_page', `${pageSize}`);
  fetchURL.searchParams.set('order_by', orderBy || 'created_at');
  fetchURL.searchParams.set('order', sortOrder || 'desc');

  if (searchTerm) {
    fetchURL.searchParams.set('q', searchTerm);
  }

  Object.entries(filters).forEach(([key, values]) => {
    values.forEach((value) =>
      fetchURL.searchParams.append('filter', `${key}=${value}`)
    );
  });

  const response = await fetchWithAuth(fetchURL.href, { signal });
  if (!response.ok) {
    throw new Error('Failed to load architect scenes');
  }
  const json = (await response.json()) as ArchitectScenesGetResponse;

  return {
    data: json.data.map(transformRawArchitectScenes),
    meta: { totalCount: json.total_count },
  };
};

export const useArchitectSceneListQuery = (
  curUserOrg: UserOrg | undefined,
  orderBy: string,
  sortOrder: string,
  pageIndex: number,
  pageSize: number,
  searchParams: string,
  enabled = true
) => {
  const {
    data: { data = [], meta } = {},
    isError,
    isRefetching,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: [
      'architect-scenes-list',
      curUserOrg?.id,
      orderBy,
      sortOrder,
      pageIndex,
      pageSize,
      searchParams,
    ],
    queryFn: ({ signal }) =>
      fetchArchitectScenes(
        curUserOrg,
        orderBy,
        sortOrder,
        pageIndex,
        pageSize,
        searchParams,
        signal
      ),
    staleTime: Infinity,
    gcTime: 900000, // Data is cached for 15 minutes
    // Keep stale rows visible while next page/filter result is loading.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!curUserOrg?.id && enabled,
  });

  return { data, meta, isError, isRefetching, isLoading, isSuccess, refetch };
};

export const useUpdateArchitectSceneTitle = (orgId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sceneId, title }: UpdateArchitectSceneTitleParams) => {
      if (!orgId) {
        throw new Error('Organization ID is not available');
      }

      const response = await fetchWithAuth(
        `${BASE_ORG_API}/${orgId}/architect_scenes/${sceneId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update architect scene title');
      }

      return transformRawArchitectScenes(
        (await response.json()) as RawArchitectSceneInfo
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['architect-scenes-list', orgId],
      });
    },
  });
};

export const usePublishArchitectScene = (orgId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sceneId, title }: PublishArchitectSceneParams) => {
      if (!orgId) {
        throw new Error('Organization ID is not available');
      }

      const response = await fetchWithAuth(
        `${BASE_ORG_API}/${orgId}/architect_scenes/${sceneId}/publication`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to publish architect scene');
      }

      return response.json() as Promise<unknown>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['architect-scenes-list', orgId],
      });
    },
  });
};

export const useUnpublishArchitectScene = (orgId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sceneId: ArchitectSceneInfo['id']) => {
      if (!orgId) {
        throw new Error('Organization ID is not available');
      }

      const response = await fetchWithAuth(
        `${BASE_ORG_API}/${orgId}/architect_scenes/${sceneId}/publication`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error('Failed to unpublish architect scene');
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['architect-scenes-list', orgId],
      });
    },
  });
};

export const useDownloadArchitectSceneFiles = (orgId: number | undefined) =>
  useMutation({
    mutationFn: async ({
      sceneId,
    }: DownloadArchitectSceneFilesParams): Promise<ArchitectSceneFilesDownloadResult> => {
      if (!orgId || sceneId === undefined) {
        throw new Error('Organization and scene IDs are required');
      }

      const filesResponse = await fetchWithAuth(
        `${BASE_ORG_API}/${orgId}/architect_scenes/${sceneId}/files/`
      );
      if (!filesResponse.ok) {
        throw new Error('Failed to load architect scene files');
      }
      const files = (await filesResponse.json()) as ArchitectSceneFile[];

      const results = await Promise.allSettled(
        files.map(async (file) => {
          const response = await fetchWithAuth(
            `${BASE_ORG_API}/${orgId}/architect_scenes/${sceneId}/files/${file.id}/download`,
            { method: 'POST' }
          );
          if (!response.ok) {
            throw new Error(
              `Failed to create download for architect scene file ${file.id}`
            );
          }

          const json = (await response.json()) as { signed_url: string };
          if (!json.signed_url) {
            throw new Error(
              `Missing download URL for architect scene file ${file.id}`
            );
          }

          return { ...file, signedUrl: json.signed_url };
        })
      );

      return {
        files: results.flatMap((result) =>
          result.status === 'fulfilled' ? [result.value] : []
        ),
        failureCount: results.filter((result) => result.status === 'rejected')
          .length,
      };
    },
  });
